import type { DeepReadonly, ToRefs } from 'vue'
import { ASKABLE_SLIDES } from '@/entities/deck'
import type { HotspotType, SiteChangeEvent } from '@/entities/site'
import type { Deck, IncludeCompany, DeckUserType, SlideType } from '@/entities/deck'
import { EmbedType } from '@/entities/embed'

export interface DeckOverlayState {
  sidebarToggle: boolean
  arrowNavigation: boolean
  exitDeck: boolean
  hostAnnotation: boolean
  annotation: boolean
  fullscreen: boolean
  threeDLayersMenu: boolean
  threeDLegends: boolean
  siteBackNavigation: boolean
  hotspotBack: boolean
  contactUs: boolean
  askAQuestion: boolean
  bookAMeeting: boolean
  meetingControls: boolean
  avTileRow: boolean
  screenShare: boolean
  gridMode: boolean
  contentOverlay: boolean
  attendeePresence: boolean
  slideTitle: boolean
  splashScreen: boolean
  vrifyLogo: boolean
}

export interface UseDeckOverlayState {
  state: Readonly<Ref<DeckOverlayState>>
}

interface Params {
  embedType: EmbedType | null
  isMeeting: boolean
  isAvSession: boolean
  isContentBeingShared: boolean
  isGridMode: boolean
  isFullscreenEnabled: boolean
  isSidebarOpen: boolean
  isAutoPlaying: boolean
  isVRIFYLogoHidden: boolean
  isSlideTransitioning: boolean
  isLayerMenuShowing: boolean
  siteHotspotType: HotspotType | ''
  deckUserType: DeckUserType
  currentSlideIndex: number
  deck: DeepReadonly<Deck & IncludeCompany>
  siteHistory: DeepReadonly<SiteChangeEvent[]>
}

const initState: DeckOverlayState = Object.freeze({
  sidebarToggle: false,
  arrowNavigation: false,
  exitDeck: true,
  hostAnnotation: false,
  annotation: false,
  fullscreen: false,
  threeDLayersMenu: false,
  threeDLegends: false,
  siteBackNavigation: false,
  hotspotBack: false,
  contactUs: false,
  askAQuestion: false,
  bookAMeeting: false,
  meetingControls: false,
  avTileRow: false,
  screenShare: false,
  gridMode: false,
  contentOverlay: false,
  attendeePresence: true,
  slideTitle: false,
  splashScreen: false,
  vrifyLogo: false,
})

export function useDeckOverlayState({
  embedType,
  isMeeting,
  isAvSession,
  isContentBeingShared,
  isGridMode,
  isFullscreenEnabled,
  isSidebarOpen,
  isAutoPlaying,
  isVRIFYLogoHidden,
  isSlideTransitioning,
  isLayerMenuShowing,
  siteHotspotType,
  deckUserType,
  deck,
  currentSlideIndex,
  siteHistory,
}: ToRefs<Params>): UseDeckOverlayState {
  const state = ref<DeckOverlayState>(initState)
  const showSlideTitle = computed<boolean>(() => {
    return !!(
      deck.value?.slides[currentSlideIndex.value]?.title_visible_in_3d_model &&
      deck.value?.slides[currentSlideIndex.value]?.title &&
      !isMeeting.value
    )
  })
  // Have to use watch instead of watchEffect because of an infinite loop bug.
  // Should be fixed when we move to Vue3
  watch(
    [
      currentSlideIndex,
      deckUserType,
      embedType,
      isMeeting,
      isAvSession,
      isContentBeingShared,
      isGridMode,
      isAutoPlaying,
      isSlideTransitioning,
      siteHotspotType,
      siteHistory,
      isSidebarOpen,
    ],
    () => {
      state.value = {
        ...initState,
        sidebarToggle: true,
        arrowNavigation: true,
      }

      switch (deck.value?.slides[currentSlideIndex.value]?.type) {
        case 'S': // Site
          state.value = {
            ...state.value,
            annotation: true,
            siteBackNavigation: !!siteHistory.value.length,
            vrifyLogo: !isVRIFYLogoHidden.value,
          }
          break

        case 'M': // 3D
          state.value = {
            ...state.value,
            annotation: true,
            threeDLayersMenu: !isSlideTransitioning.value && (isLayerMenuShowing.value || deckUserType.value === 'host'),
            threeDLegends: !isSlideTransitioning.value,
            vrifyLogo: !isVRIFYLogoHidden.value,
            slideTitle: showSlideTitle.value,
          }
          break

        case 'E': // Meetings
        case 'V': // Video
        case 'D': // Dynamic/URL
          break

        default:
          state.value = {
            ...state.value,
            annotation: true,
          }
          break
      }

      if (isMeeting.value) {
        state.value = {
          ...state.value,
          exitDeck: false,
          meetingControls: true,
        }
      }

      if (!isSidebarOpen.value && !deckUserType.value) {
        let bookAMeeting = deck.value.show_bam_sidebar_button && !!deck.value.request_setting?.is_active
        let askAQuestion = deck.value.show_aaq_button && _isAskableSlide(deck.value?.slides[currentSlideIndex.value]?.type)

        state.value = { ...state.value, askAQuestion, bookAMeeting, contactUs: askAQuestion && bookAMeeting }
      } else {
        state.value = { ...state.value, askAQuestion: false, bookAMeeting: false, contactUs: false }
      }

      if (siteHotspotType.value && '3NIPV'.includes(siteHotspotType.value)) {
        state.value = {
          ...state.value,
          siteBackNavigation: false,
          annotation: siteHotspotType.value === '3',
          threeDLayersMenu: siteHotspotType.value === '3',
          threeDLegends: siteHotspotType.value === '3',
          hotspotBack: true,
          exitDeck: false,
          arrowNavigation: false,
        }
      }

      if (embedType.value) {
        state.value = {
          ...state.value,
          fullscreen: isFullscreenEnabled.value,
          annotation: false,
          exitDeck: false,
          splashScreen: true,
          threeDLayersMenu: false,
          threeDLegends: true,
        }
      }

      if (embedType.value === EmbedType.Partner || embedType.value === EmbedType.Animated) {
        state.value.splashScreen = false
      }

      if (isAvSession.value) {
        state.value = {
          ...state.value,
          avTileRow: true,
        }

        if (isGridMode.value) {
          state.value = {
            ...state.value,
            avTileRow: false,
            gridMode: true,
            contentOverlay: true,
            arrowNavigation: false,
            annotation: false,
            threeDLayersMenu: false,
            threeDLegends: false,
            hotspotBack: false,
            siteBackNavigation: false,
          }
        }

        if (isContentBeingShared.value) {
          state.value = {
            ...state.value,
            avTileRow: true,
            gridMode: false,
            screenShare: true,
            contentOverlay: true,
            arrowNavigation: false,
            annotation: false,
            threeDLayersMenu: false,
            threeDLegends: false,
            hotspotBack: false,
            siteBackNavigation: false,
          }
        }
      }

      // Hide logo for host
      if (deckUserType.value === 'host') {
        state.value = { ...state.value, vrifyLogo: false }
      }

      if (deckUserType.value === 'host' && state.value.annotation) {
        state.value = {
          ...state.value,
          annotation: false,
          hostAnnotation: true,
        }
      }

      if (deckUserType.value === 'controlled') {
        state.value = {
          ...state.value,
          sidebarToggle: false,
          arrowNavigation: false,
          annotation: false,
          threeDLayersMenu: false,
          threeDLegends: !isGridMode.value && !isContentBeingShared.value,
          hotspotBack: false,
          siteBackNavigation: false,
        }
      }

      if (isAutoPlaying.value) {
        state.value = {
          ...state.value,
          sidebarToggle: false,
          arrowNavigation: false,
          exitDeck: false,
          hostAnnotation: false,
          annotation: false,
          fullscreen: false,
          threeDLayersMenu: false,
          threeDLegends: false,
          siteBackNavigation: false,
          hotspotBack: false,
          contactUs: false,
          askAQuestion: false,
          bookAMeeting: false,
          meetingControls: false,
          avTileRow: false,
          screenShare: false,
          gridMode: false,
          contentOverlay: false,
          attendeePresence: true,
          slideTitle: showSlideTitle.value,
          splashScreen: false,
        }
      }
    },
    { immediate: true },
  )

  return { state: readonly(state) }
}

function _isAskableSlide(slideType?: SlideType) {
  if (!slideType) return false
  return ASKABLE_SLIDES.includes(slideType)
}
